"use client";

import React, { useMemo, forwardRef, useImperativeHandle, useEffect } from "react";
import { ErrorMessage } from "@hookform/error-message";
import { useFieldArray } from "react-hook-form";
import {
  formatCallingCodeOption,
  renderErrorMessage,
  handleRemove,
  setPrimaryField,
} from "src/lib/utils";

import {
  AccountResponse,
  ActivityResponse,
  ContactResponse,
  CountryType,
  LeadResponse,
  OpportunityResponse,
  PhoneFormData,
  SiteResponse,
} from "@app/types";

import { useFormContext } from "./Form";
import { PhoneNumberField } from "./PhoneNumberField";

type PhonesProps = {
  name: string;
  countries: CountryType[];
  entity:
    | AccountResponse
    | LeadResponse
    | OpportunityResponse
    | ContactResponse
    | SiteResponse;
  updateState: React.Dispatch<React.SetStateAction<ActivityResponse[]>>;
  opportunity?: OpportunityResponse;
  disablePrimary?: boolean;
  numberRequired?: number;
  hideTitle?: boolean;
  placeholder?: string;
};

export type PhonesRef = {
  appendPhone: (data: Partial<PhoneFormData>) => void;
};

export const Phones = forwardRef<PhonesRef, PhonesProps>(function Phones(
  {
    name,
    entity,
    updateState,
    opportunity,
    placeholder = "No phone numbers provided",
    hideTitle = false,
    numberRequired = 0,
    disablePrimary = false,
    countries,
  }: PhonesProps,
  ref
) {
  const {
    control,
    formState: { errors },
    isEditing,
    setError,
    clearErrors,
    getValues,
    setValue,
  } = useFormContext();

  const {
    fields: phoneNumbers,
    append: appendPhone,
    remove: removePhone,
  } = useFieldArray({
    control,
    name,
  });

  // Validation function that can be called directly
  const validatePhoneNumbers = () => {
    if (isEditing && numberRequired && phoneNumbers.length < numberRequired) {
      setError(`${name}.arrayError`, {
        type: 'manual',
        message:
          numberRequired <= 1
            ? `You must proved at least ${numberRequired} phone number`
            : `You must proved at least ${numberRequired} phone numbers`,
      })
    } else {
      clearErrors(name)
    }
  };
  useEffect(() => {
    if (isEditing && numberRequired) {
      validatePhoneNumbers();
    }
  }, [isEditing, numberRequired, phoneNumbers.length, name]);

  useImperativeHandle(ref, () => ({
    appendPhone: (data: Partial<PhoneFormData>) => {
      appendPhone(data);  
    },
  }));

  function resetFieldArrayEntry(index: number) {
    setValue(`${name}.${index}.label`, '')
    setValue(`${name}.${index}.number`, '')
    setValue(`${name}.${index}.id`, '')
  }

  //logic interception to check for field requirement before allowing user to remove required number
  function handleRemovePhone(index: number) {
    const primaryIndex = getValues('setPrimaryPhone')
    handleRemove({
      entity: 'phone number',
      index: index as number,
      primaryIndex: primaryIndex,
      numberRequired: numberRequired,
      primaryField: 'setPrimaryPhone',
      name: `${name}.arrayError`,
      array: phoneNumbers,
      setError: setError,
      clearError: clearErrors,
      removeItem: removePhone,
      setField: setPrimaryField,
      resetFields: resetFieldArrayEntry,
      setValue: setValue,
    })
  }

  const callingCodeOptionsMap = useMemo(() => {
    return new Map(countries.map((country) => [country.alpha2code, formatCallingCodeOption(country)]))
  }, [countries])

  return (
    <div>
      <div className="px-0">
        {phoneNumbers.length ? (
          phoneNumbers.map((field, i) => (
            <div key={field.id}>
              {i > 0 && (
                <hr className="border-t border-gray-200 py-1 my-[6px]" />
              )}
              <div className="pb-4">
                <PhoneNumberField
                  numberRequired={numberRequired}
                  opportunity={opportunity ?? null}
                  updateState={updateState}
                  entity={entity}
                  removePhone={handleRemovePhone}
                  phoneIndex={i}
                  countries={countries}
                  group={name}
                  name={`${name}.${i}`}
                  disablePrimary={disablePrimary}
                />
              </div>
            </div>
          ))
        ) : (
          <span className="text-sm text-gray-dark">{placeholder}</span>
        )}
      </div>
      <div className="w-fit">
        <ErrorMessage errors={errors} name={`${name}.arrayError`} render={renderErrorMessage} />
      </div>
    </div>
  );
});
